<template>
  <v-dialog
    v-model="dialogProxy"
    :max-width="showPreview ? 'calc(100% - 80px)' : '1200px'"
    persistent
    scrollable
  >
    <v-card>
      <v-card-title class="headline d-flex align-center">
        <span>Warranty Certificate</span>
        <v-spacer />

        <v-btn
          v-if="showPreview"
          icon
          class="mr-2"
          @click="showPreview = false"
        >
          <v-icon>mdi-eye-off</v-icon>
        </v-btn>

        <v-btn icon @click="closeDialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider />

      <v-card-text class="pa-0">
        <div class="warranty-dialog-layout">
          <div :class="['warranty-form-pane', showPreview ? 'with-preview' : '']">
            <div class="pa-6">
              <div class="section-title mb-2">Seller Information</div>

              <v-row dense>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="form.seller_name"
                    label="Seller Name *"
                    outlined
                    dense
                    :error-messages="getValidationError('seller_name')"
                    @input="clearValidationError('seller_name')"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="form.seller_tax_number"
                    label="Seller Tax Number"
                    outlined
                    dense
                    :class="{ 'optional-warning-field': hasOptionalWarning('seller_tax_number') }"
                    @input="clearOptionalWarning('seller_tax_number')"
                  />
                </v-col>
                <v-col cols="12">
                  <v-textarea
                    v-model="form.seller_address"
                    label="Seller Address *"
                    outlined
                    dense
                    rows="2"
                    auto-grow
                    :error-messages="getValidationError('seller_address')"
                    @input="clearValidationError('seller_address')"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="form.seller_phone"
                    label="Phone Number"
                    outlined
                    dense
                    :class="{ 'optional-warning-field': hasOptionalWarning('seller_phone') }"
                    @input="clearOptionalWarning('seller_phone')"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="form.seller_email"
                    label="E-mail"
                    outlined
                    dense
                    :class="{ 'optional-warning-field': hasOptionalWarning('seller_email') }"
                    @input="clearOptionalWarning('seller_email')"
                  />
                </v-col>
              </v-row>

              <div class="section-title mt-4 mb-2">Buyer Information</div>

              <v-row dense>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="form.buyer_name"
                    label="Buyer Name *"
                    outlined
                    dense
                    :error-messages="getValidationError('buyer_name')"
                    @input="clearValidationError('buyer_name')"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="form.buyer_tax_number"
                    label="Buyer Tax Number"
                    outlined
                    dense
                    :class="{ 'optional-warning-field': hasOptionalWarning('buyer_tax_number') }"
                    @input="clearOptionalWarning('buyer_tax_number')"
                  />
                </v-col>
                <v-col cols="12">
                  <v-textarea
                    v-model="form.buyer_address"
                    label="Buyer Address *"
                    outlined
                    dense
                    rows="2"
                    auto-grow
                    :error-messages="getValidationError('buyer_address')"
                    @input="clearValidationError('buyer_address')"
                  />
                </v-col>
              </v-row>

              <div class="section-title mt-4 mb-2">Document Data</div>

              <v-row dense>
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="form.invoice_number"
                    label="Invoice Number"
                    outlined
                    dense
                    :class="{ 'optional-warning-field': hasOptionalWarning('invoice_number') }"
                    @input="clearOptionalWarning('invoice_number')"
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <v-menu
                    v-model="completionDateMenu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="form.completion_date"
                        label="Completion Date"
                        outlined
                        dense
                        readonly
                        :class="{ 'optional-warning-field': hasOptionalWarning('completion_date') }"
                        v-bind="attrs"
                        v-on="on"
                      />
                    </template>

                    <v-date-picker
                      v-model="form.completion_date"
                      no-title
                      scrollable
                      @input="onCompletionDateSelected"
                    />
                  </v-menu>
                </v-col>
                <v-col cols="12" md="4">
                  <v-menu
                    v-model="issuedDateMenu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="form.issued_date"
                        label="Issue Date"
                        outlined
                        dense
                        readonly
                        :class="{ 'optional-warning-field': hasOptionalWarning('issued_date') }"
                        v-bind="attrs"
                        v-on="on"
                      />
                    </template>

                    <v-date-picker
                      v-model="form.issued_date"
                      no-title
                      scrollable
                      @input="onIssuedDateSelected"
                    />
                  </v-menu>
                </v-col>
              </v-row>

              <div class="section-title mt-4 mb-2">Items</div>

              <v-alert
                v-if="getValidationError('items').length"
                dense
                text
                type="warning"
                class="mb-3"
              >
                {{ getValidationError('items')[0] }}
              </v-alert>

              <v-row
                v-for="(item, index) in form.items"
                :key="'item-' + index"
                dense
                class="mb-2"
              >
                <v-col cols="12" md="5">
                  <v-text-field
                    v-model="item.name"
                    label="Name *"
                    outlined
                    dense
                    hide-details="auto"
                    :error-messages="getItemValidationError(index, 'name')"
                    @input="clearItemValidationError(index, 'name')"
                  />
                </v-col>

                <v-col cols="12" md="2">
                  <v-text-field
                    v-model="item.quantity"
                    label="Quantity *"
                    outlined
                    dense
                    hide-details="auto"
                    :error-messages="getItemValidationError(index, 'quantity')"
                    @input="clearItemValidationError(index, 'quantity')"
                  />
                </v-col>

                <v-col cols="12" md="2">
                  <v-text-field
                    v-model="item.unit"
                    label="Unit *"
                    outlined
                    hide-details="auto"
                    dense
                    :error-messages="getItemValidationError(index, 'unit')"
                    @input="clearItemValidationError(index, 'unit')"
                  />
                </v-col>

                <v-col cols="12" md="2">
                  <v-menu
                    v-model="warrantyUntilMenus[index]"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="item.warranty_until"
                        label="Warranty Until"
                        outlined
                        hide-details
                        dense
                        readonly
                        :class="{ 'optional-warning-field': hasItemOptionalWarning(index, 'warranty_until') }"
                        v-bind="attrs"
                        v-on="on"
                      />
                    </template>

                    <v-date-picker
                      v-model="item.warranty_until"
                      no-title
                      scrollable
                      @input="onWarrantyUntilSelected(index)"
                    />
                  </v-menu>
                </v-col>

                <v-col cols="12" md="1" class="d-flex align-center">
                  <v-btn icon color="red" @click="removeItem(index)">
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </v-col>
              </v-row>

              <v-btn small outlined color="primary" class="mb-4" @click="addItem">
                <v-icon left>mdi-plus</v-icon>
                Add Item
              </v-btn>
            </div>
          </div>

          <div v-if="showPreview" class="warranty-preview-pane">
            <div class="warranty-preview-toolbar">
              <div class="subtitle-2 font-weight-medium">Preview</div>

              <v-progress-circular
                v-if="previewLoading"
                indeterminate
                size="18"
                width="2"
                color="primary"
              />
            </div>

            <iframe
              v-if="previewHtml"
              :srcdoc="previewHtml"
              class="warranty-preview-frame"
              frameborder="0"
            />

            <div v-else class="warranty-preview-empty">
              No preview available.
            </div>
          </div>
        </div>
      </v-card-text>

      <v-divider />

      <v-card-actions class="pa-4">
        <v-btn
          text
          color="primary"
          :loading="previewLoading"
          @click="openPreview"
        >
          {{ showPreview ? "Hide Preview" : "Open Preview" }}
        </v-btn>

        <v-spacer />

        <v-btn text @click="closeDialog">Cancel</v-btn>

        <v-btn
          color="primary"
          :loading="loading"
          @click="generateDocument"
        >
          Generate
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import ApiService from "@/core/services/api.service";
import Swal from "sweetalert2";


const DOCUMENT_TYPE = "warranty_certificate";

export default {
  name: "WarrantyCertificateDialog",

  props: {
    value: {
      type: Boolean,
      default: false,
    },
    salesOrderId: {
      type: [Number, String],
      required: true,
    },
  },

  data() {
    return {
      loading: false,
      previewLoading: false,
      showPreview: false,
      previewHtml: "",
      previewDebounceTimer: null,
      previewRequestId: 0,
      completionDateMenu: false,
      issuedDateMenu: false,
      warrantyUntilMenus: [],
      validationErrors: {},
      optionalWarnings: {},
      form: {
        seller_name: "",
        seller_address: "",
        seller_tax_number: "",
        seller_phone: "",
        seller_email: "",
        buyer_name: "",
        buyer_address: "",
        buyer_tax_number: "",
        invoice_number: "",
        completion_date: "",
        issued_date: "",
        items: [],
      },
    };
  },

  computed: {
    dialogProxy: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
  },

  watch: {
    value(val) {
      if (val) {
        this.loadDefaults();
      } else {
        this.resetPreview();
      }
    },

    form: {
      deep: true,
      handler() {
        if (!this.showPreview) {
          return;
        }

        this.schedulePreviewRefresh();
      },
    },
    "form.completion_date":{
      handler(val, oldVal){
        if (!val || !oldVal) {
          return;
        }
      
        const [year, month, day] = val.split("-");
      
        const warrantyUntil = `${Number(year) + 1}-${month}-${day}`;
      
        this.form.items.forEach((element) => {
          element.warranty_until = warrantyUntil;
        });
      }
    }
  },

  methods: {
    async loadDefaults() {
      try {
        const { data } = await ApiService.get(
          `sales/salesOrder/${this.salesOrderId}/generated-documents/${DOCUMENT_TYPE}/defaults`
        );

        this.form = {
          seller_name: data.seller_name || "",
          seller_address: data.seller_address || "",
          seller_tax_number: data.seller_tax_number || "",
          seller_phone: data.seller_phone || "",
          seller_email: data.seller_email || "",
          buyer_name: data.buyer_name || "",
          buyer_address: data.buyer_address || "",
          buyer_tax_number: data.buyer_tax_number || "",
          invoice_number: data.invoice_number || "",
          completion_date: data.completion_date || "",
          issued_date: data.issued_date || "",
          items: Array.isArray(data.items) ? data.items.filter(item => !String(item.name).includes('Szállítási díj')) : [],
        };

        this.warrantyUntilMenus = this.form.items.map(() => false);
        this.validationErrors = {};
        this.optionalWarnings = {};
      } catch (e) {
        console.log(e);
      }
    },

    isEmpty(value) {
      return value === null || value === undefined || String(value).trim() === "";
    },

    hasOptionalWarning(field) {
      return Boolean(this.optionalWarnings[field]);
    },

    hasItemOptionalWarning(index, field) {
      return Boolean(this.optionalWarnings[this.getItemValidationKey(index, field)]);
    },

    clearOptionalWarning(field) {
      if (this.optionalWarnings[field]) {
        this.$delete(this.optionalWarnings, field);
      }
    },

    clearItemOptionalWarning(index, field) {
      const key = this.getItemValidationKey(index, field);

      if (this.optionalWarnings[key]) {
        this.$delete(this.optionalWarnings, key);
      }
    },

    getValidationError(field) {
      return this.validationErrors[field] || [];
    },

    getItemValidationKey(index, field) {
      return `items.${index}.${field}`;
    },

    getItemValidationError(index, field) {
      return this.validationErrors[this.getItemValidationKey(index, field)] || [];
    },

    clearValidationError(field) {
      if (this.validationErrors[field]) {
        this.$delete(this.validationErrors, field);
      }
    },

    clearItemValidationError(index, field) {
      const key = this.getItemValidationKey(index, field);

      if (this.validationErrors[key]) {
        this.$delete(this.validationErrors, key);
      }
    },

    validateForm() {
      const errors = {};
      const requiredFields = [
        { key: "seller_name", label: "Seller Name" },
        { key: "seller_address", label: "Seller Address" },
        { key: "buyer_name", label: "Buyer Name" },
        { key: "buyer_address", label: "Buyer Address" },
      ];

      requiredFields.forEach((field) => {
        if (this.isEmpty(this.form[field.key])) {
          errors[field.key] = [`${field.label} is required.`];
        }
      });

      if (!Array.isArray(this.form.items) || this.form.items.length === 0) {
        errors.items = ["At least one item is required."];
      } else {
        this.form.items.forEach((item, index) => {
          [
            { key: "name", label: "Item name" },
            { key: "quantity", label: "Quantity" },
            { key: "unit", label: "Unit" },
          ].forEach((field) => {
            if (this.isEmpty(item[field.key])) {
              errors[this.getItemValidationKey(index, field.key)] = [
                `${field.label} is required.`,
              ];
            }
          });
        });
      }

      this.validationErrors = errors;

      return Object.keys(errors).length === 0;
    },

    async showValidationAlert() {
      await Swal.fire({
        title: "Missing required data",
        icon: "warning",
        text: "Please fill in all required fields before creating the document.",
        confirmButtonText: "OK",
      });
    },

    getMissingOptionalFields() {
      const missingFields = [];
      const optionalFields = [
        { key: "seller_tax_number", label: "Seller Tax Number" },
        { key: "seller_phone", label: "Phone Number" },
        { key: "seller_email", label: "E-mail" },
        { key: "buyer_tax_number", label: "Buyer Tax Number" },
        { key: "invoice_number", label: "Invoice Number" },
        { key: "completion_date", label: "Completion Date" },
        { key: "issued_date", label: "Issue Date" },
      ];

      optionalFields.forEach((field) => {
        if (this.isEmpty(this.form[field.key])) {
          missingFields.push(field);
        }
      });

      if (Array.isArray(this.form.items)) {
        this.form.items.forEach((item, index) => {
          if (this.isEmpty(item.warranty_until)) {
            missingFields.push({
              key: this.getItemValidationKey(index, "warranty_until"),
              label: `Item ${index + 1} - Warranty Until`,
            });
          }
        });
      }

      return missingFields;
    },

    markOptionalWarnings(fields) {
      const warnings = {};

      fields.forEach((field) => {
        warnings[field.key] = true;
      });

      this.optionalWarnings = warnings;
    },

    async confirmMissingOptionalFields() {
      const missingFields = this.getMissingOptionalFields();

      if (!missingFields.length) {
        this.optionalWarnings = {};
        return true;
      }

      const fieldList = missingFields
        .map((field) => `<li>${field.label}</li>`)
        .join("");

      const result = await Swal.fire({
        title: "Some fields are empty",
        icon: "warning",
        html: `The following fields are not filled:<br><ul style="text-align:left;margin-top:12px;">${fieldList}</ul><br>Do you want to continue anyway?`,
        showCancelButton: true,
        confirmButtonText: "Yes, generate document",
        cancelButtonText: "No, I will fill them",
        confirmButtonColor: "#00c300",
        width: "650px",
      });

      if (result.isConfirmed) {
        this.optionalWarnings = {};
        return true;
      }

      this.markOptionalWarnings(missingFields);
      return false;
    },

    onCompletionDateSelected() {
      this.completionDateMenu = false;
      this.clearOptionalWarning("completion_date");
    },

    onIssuedDateSelected() {
      this.issuedDateMenu = false;
      this.clearOptionalWarning("issued_date");
    },

    onWarrantyUntilSelected(index) {
      this.$set(this.warrantyUntilMenus, index, false);
      this.clearItemOptionalWarning(index, "warranty_until");
    },

    addItem() {
      this.form.items.push({
        name: "",
        quantity: 1,
        unit: "db",
        warranty_until: "",
      });

      this.warrantyUntilMenus.push(false);
    },

    removeItem(index) {
      this.form.items.splice(index, 1);
      this.warrantyUntilMenus.splice(index, 1);
    },

    resetPreview() {
      this.showPreview = false;
      this.previewHtml = "";
      this.previewLoading = false;
      this.validationErrors = {};
      this.optionalWarnings = {};

      if (this.previewDebounceTimer) {
        clearTimeout(this.previewDebounceTimer);
        this.previewDebounceTimer = null;
      }
    },

    closeDialog() {
      this.resetPreview();
      this.dialogProxy = false;
    },

    schedulePreviewRefresh() {
      if (this.previewDebounceTimer) {
        clearTimeout(this.previewDebounceTimer);
      }

      this.previewDebounceTimer = setTimeout(() => {
        this.refreshPreview();
      }, 400);
    },

    async openPreview() {
      if (this.showPreview === true){
        this.showPreview = false;
      }else{
        this.showPreview = true
      }
      await this.refreshPreview();
    },

    async refreshPreview() {
      const requestId = ++this.previewRequestId;
      this.previewLoading = true;

      try {
        const { data } = await ApiService.post(
          `sales/salesOrder/${this.salesOrderId}/generated-documents/${DOCUMENT_TYPE}/preview`,
          this.form
        );

        if (requestId !== this.previewRequestId) {
          return;
        }

        this.previewHtml = data.html || "";
      } catch (error) {
        console.log(error);
      } finally {
        if (requestId === this.previewRequestId) {
          this.previewLoading = false;
        }
      }
    },

    async generateDocument() {
      if (!this.validateForm()) {
        await this.showValidationAlert();
        return;
      }

      const missingFields = this.getMissingOptionalFields();

      this.markOptionalWarnings(missingFields);

      const canContinue = await this.confirmMissingOptionalFields();


      if (!canContinue) {
        return;
      }

      this.loading = true;

      try {
        const { data } = await ApiService.post(
          `sales/salesOrder/${this.salesOrderId}/generated-documents/${DOCUMENT_TYPE}/generate`,
          this.form
        );

        if (data && data.url) {
          window.open(data.url, "_blank");
        }

        this.$emit("generated", data);
        this.closeDialog();
      } catch (error) {
        console.error(error);
      
        Swal.fire({
          title: "Error",
          icon: "error",
          text: "The document could not be created.",
        });
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.warranty-dialog-layout {
  display: flex;
  min-height: 70vh;
}

.warranty-form-pane {
  flex: 0 0 100%;
  max-width: 100%;
  border-right: 1px solid #e0e0e0;
  overflow-y: auto;
}
.warranty-form-pane.with-preview {
  flex: 0 0 40%;
  max-width: 40%;
}

.warranty-preview-pane {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  background: #f5f5f5;
  padding: 16px;
  min-width: 0;
}

.warranty-preview-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.warranty-preview-frame {
  width: 100%;
  height: calc(70vh - 32px);
  background: #fff;
  border: 1px solid #dcdcdc;
  border-radius: 8px;
}

.warranty-preview-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  height: calc(70vh - 32px);
  background: #fff;
  border: 1px solid #dcdcdc;
  border-radius: 8px;
  color: #757575;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
}

.optional-warning-field >>> .v-input__slot {
  background-color: #fff8e1 !important;
}

.optional-warning-field >>> fieldset {
  border-color: #f9a825 !important;
}
</style>